import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'
import { selectCourse, setGpsEditorTutorialPhase } from 'store/course/slice'
import {
  GPS_EDITOR_TUTORIAL,
  getGpsEditorTutorialPhaseData,
  gpsEditorTutorialToolbarPhaseContent,
} from 'store/course/utils'
import { useEffect } from 'react'
import { FormattedMessageWrapper } from 'components/ui/FormattedMessageWrapper'

interface Props {
  enabled?: boolean
  showHalfwayDialog: boolean
  showFinishedDialog: boolean
}

export default function CourseGpsEditorTutorial({
  enabled = true,
  showHalfwayDialog,
  showFinishedDialog,
}: Props): JSX.Element | null {
  const intl = useIntl()
  const dispatch = useDispatch()
  const { gpsEditorTutorialPhase } = useSelector(selectCourse)

  useEffect(() => {
    if (showHalfwayDialog) {
      dispatch(setGpsEditorTutorialPhase(GPS_EDITOR_TUTORIAL.Halfway))
    }
    if (showFinishedDialog) {
      dispatch(setGpsEditorTutorialPhase(GPS_EDITOR_TUTORIAL.Finish))
    }
  }, [dispatch, showHalfwayDialog, showFinishedDialog])

  if (!enabled || gpsEditorTutorialPhase === null || gpsEditorTutorialPhase === GPS_EDITOR_TUTORIAL.Done) {
    return null
  }

  const tutorialData: GpsEditorTutorialPhaseData = getGpsEditorTutorialPhaseData(gpsEditorTutorialPhase)

  const goToPhase = (phase: GpsEditorTutorialPhase | null) => {
    dispatch(setGpsEditorTutorialPhase(phase))
  }

  const renderButtons = (): JSX.Element => {
    const { primary, secondary } = tutorialData.buttons
    if (secondary === undefined) {
      return (
        <SingleButton onClick={() => goToPhase(primary.goTo)}>{intl.formatMessage({ id: primary.label })}</SingleButton>
      )
    }

    return (
      <ButtonsContainer>
        <LeftButton onClick={() => goToPhase(secondary.goTo)}>{intl.formatMessage({ id: secondary.label })}</LeftButton>
        <RightButton onClick={() => goToPhase(primary.goTo)}>{intl.formatMessage({ id: primary.label })}</RightButton>
      </ButtonsContainer>
    )
  }

  const renderToolbarTextContent = (): JSX.Element => {
    const elements: JSX.Element[] = []
    gpsEditorTutorialToolbarPhaseContent.forEach((content: GpsEditorTutorialToolbarPhaseContent) => {
      elements.push(
        <ToolbarRow>
          <ToolbarImage src={content.image} />
          <FormattedMessageWrapper id={content.text} />
        </ToolbarRow>,
      )
    })
    return <>{elements}</>
  }

  return (
    <Container>
      <Content>
        <Title>{intl.formatMessage({ id: tutorialData.title })}</Title>
        {tutorialData.progress && (
          <ProgressBar>
            <Progress style={{ width: `${tutorialData.progress}%` }} />
          </ProgressBar>
        )}
        <Text>
          {tutorialData.phase === GPS_EDITOR_TUTORIAL.Toolbar ? (
            renderToolbarTextContent()
          ) : (
            <FormattedMessageWrapper id={tutorialData.text} />
          )}
        </Text>
        {tutorialData.image && <Image src={tutorialData.image} alt="" />}
      </Content>
      {renderButtons()}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background-color: #222a35;
  color: #fff;
  position: fixed;
  width: 312px;
  min-height: 186px;
  bottom: 70px;
  left: 70px;
  border-radius: 12px;
  gap: 9px;

  & > img {
    width: 100%;
  }
`

const ProgressBar = styled.div`
  height: 3px;
  background-color: #4f6074;
  border-radius: 4px;
  margin-top: 20px;
  width: 100%;
`

const Progress = styled.div`
  height: 100%;
  background-color: #0099ff;
`

const Content = styled.div`
  padding: 24px 24px 0px 24px;
`

const Title = styled.h5`
  font-size: 18px;
  font-weight: 700;
  line-height: 21px;
  text-align: center;
`

const Text = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  opacity: 0.7;
  padding: 20px 0;
  & > p {
    margin-bottom: 22px;
  }
`

const Image = styled.img`
  max-width: 100% !important;
`

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 0;
`

const ToolbarImage = styled.img`
  width: 34px%;
  height: 34px;
  margin-right: 20px;
`

const SingleButton = styled.button`
  height: 59px;
  background-color: #fff;
  color: #404040;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  text-align: center;
  cursor: pointer;
  border: none;
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
`

const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  height: 59px;
`

const BaseButton = styled.button`
  width: 50%;
  height: 100%;
  background-color: inherit;
  border: none;
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 19px;
  text-align: center;
  cursor: pointer;
  opacity: 0.7;

  &:hover {
    opacity: 1;
  }
`

const LeftButton = styled(BaseButton)``
const RightButton = styled(BaseButton)``
